/**
 * PaymentDrawer Component
 * Record a payment against an invoice
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { CreditCard, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useRecordInvoicePaymentMutation } from '../../services/invoicingApi';
import type { Invoice, PaymentMethod, RecordPaymentRequest } from '../../types/invoice';
import { PAYMENT_METHOD_LABELS } from '../../types/invoice';
import { CurrencyInput, DatePicker, Dropdown, Input, TextArea } from '../forms';

// ============================================================================
// TYPES
// ============================================================================

export interface PaymentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onSuccess?: () => void;
}

interface PaymentFormData {
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  notes: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PAYMENT_METHOD_OPTIONS = (
  Object.entries(PAYMENT_METHOD_LABELS) as [PaymentMethod, string][]
).map(([value, label]) => ({ value, label }));

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const paymentSchema = Yup.object({
  amount: Yup.number()
    .positive('Amount must be greater than 0')
    .required('Amount is required'),
  paymentDate: Yup.string().required('Payment date is required'),
  paymentMethod: Yup.string()
    .oneOf(['check', 'ach', 'wire', 'credit_card', 'cash', 'other'])
    .required('Payment method is required'),
  referenceNumber: Yup.string().max(100).optional(),
  notes: Yup.string().max(500).optional(),
});

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const PaymentDrawer: React.FC<PaymentDrawerProps> = ({
  isOpen,
  onClose,
  invoice,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [recordPayment, { isLoading }] = useRecordInvoicePaymentMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PaymentFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(paymentSchema) as any,
    defaultValues: {
      amount: invoice?.balanceDue ?? 0,
      paymentDate: todayStr(),
      paymentMethod: 'check',
      referenceNumber: '',
      notes: '',
    },
  });

  // Reset form whenever the invoice changes
  useEffect(() => {
    if (invoice) {
      reset({
        amount: invoice.balanceDue,
        paymentDate: todayStr(),
        paymentMethod: 'check',
        referenceNumber: '',
        notes: '',
      });
    }
  }, [invoice, reset]);

  const watchedAmount = watch('amount') || 0;
  const remainingAfter =
    invoice ? Math.max(0, invoice.balanceDue - watchedAmount) : 0;

  const onSubmit = async (data: PaymentFormData) => {
    if (!projectId || !invoice) return;

    try {
      const payload: RecordPaymentRequest = {
        amount: data.amount,
        paymentDate: data.paymentDate,
        paymentMethod: data.paymentMethod,
        referenceNumber: data.referenceNumber || undefined,
        notes: data.notes || undefined,
      };

      await recordPayment({
        projectId,
        invoiceId: invoice._id,
        data: payload,
      }).unwrap();

      toast.success('Payment recorded successfully');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Failed to record payment');
    }
  };

  if (!isOpen || !invoice) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer – small (60% width) */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-50">
                <CreditCard className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Record Payment</h2>
                <p className="text-xs text-gray-500">{invoice.invoiceNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Invoice Summary */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Invoice Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Client</span>
                  <span className="font-medium text-gray-900">{invoice.clientName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Invoice Total</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(invoice.total, invoice.currency)}
                  </span>
                </div>
                {invoice.amountPaid > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Previously Paid</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(invoice.amountPaid, invoice.currency)}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 flex justify-between text-sm font-semibold">
                  <span className="text-gray-700">Balance Due</span>
                  <span className="text-red-600">
                    {formatCurrency(invoice.balanceDue, invoice.currency)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <form id="payment-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    label="Payment Amount"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.amount?.message}
                    placeholder="0.00"
                  />
                )}
              />

              {watchedAmount > 0 && watchedAmount <= invoice.balanceDue && (
                <div className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2">
                  Remaining balance after payment:{' '}
                  <span className="font-semibold text-blue-700">
                    {formatCurrency(remainingAfter, invoice.currency)}
                  </span>
                  {remainingAfter === 0 && (
                    <span className="ml-1 text-green-700 font-semibold">
                      — Invoice will be fully paid
                    </span>
                  )}
                </div>
              )}

              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Payment Date"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.paymentDate?.message}
                  />
                )}
              />

              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="Payment Method"
                    value={field.value}
                    onChange={val => field.onChange(val)}
                    options={PAYMENT_METHOD_OPTIONS}
                    error={errors.paymentMethod?.message}
                  />
                )}
              />

              <Input
                label="Reference Number (optional)"
                {...register('referenceNumber')}
                error={errors.referenceNumber?.message}
                placeholder="Check #, transaction ID, etc."
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    label="Notes (optional)"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.notes?.message}
                    placeholder="Any additional payment notes"
                    rows={3}
                  />
                )}
              />
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="payment-form"
              disabled={isLoading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default PaymentDrawer;
